import { format, parseISO, formatDistanceToNow } from 'date-fns';
import { ja } from 'date-fns/locale';
import { WIDGET_URL } from '../config/constants';

// ==========================================
// トライアル残日数（JST暦日差）
// ==========================================
export function getTrialRemainingDays(trialEndsAt: string | null | undefined): number {
  if (!trialEndsAt) return 0;
  const fmt = new Intl.DateTimeFormat('ja-JP', {
    timeZone: 'Asia/Tokyo',
    year: 'numeric', month: '2-digit', day: '2-digit'
  });
  const todayParts = fmt.formatToParts(new Date());
  const endParts = fmt.formatToParts(new Date(trialEndsAt));
  const toDay = (parts: Intl.DateTimeFormatPart[]) => {
    const y = parts.find(p => p.type === 'year')!.value;
    const m = parts.find(p => p.type === 'month')!.value;
    const d = parts.find(p => p.type === 'day')!.value;
    return Date.UTC(+y, +m - 1, +d);
  };
  const diffDays = Math.round((toDay(endParts) - toDay(todayParts)) / 86400000);
  return Math.max(0, diffDays);
}

// ==========================================
// 日付フォーマット
// ==========================================
export const formatDate = (dateString: string | null | undefined, formatStr: string = 'yyyy/MM/dd'): string => {
  if (!dateString) return '-';
  try {
    return format(parseISO(dateString), formatStr, { locale: ja });
  } catch {
    return '-';
  }
};

export const formatDateTime = (dateString: string | null | undefined): string => {
  return formatDate(dateString, 'yyyy/MM/dd HH:mm');
};

export const formatRelativeTime = (dateString: string | null | undefined): string => {
  if (!dateString) return '-';
  try {
    return formatDistanceToNow(parseISO(dateString), { addSuffix: true, locale: ja });
  } catch {
    return '-';
  }
};

// ==========================================
// 数値フォーマット
// ==========================================
export const formatNumber = (num: number | null | undefined): string => {
  if (num === null || num === undefined) return '-';
  return num.toLocaleString('ja-JP');
};

export const formatCurrency = (amount: number | null | undefined): string => {
  if (amount === null || amount === undefined) return '-';
  return `¥${amount.toLocaleString('ja-JP')}`;
};

export const formatPercentage = (value: number | null | undefined, decimals: number = 1): string => {
  if (value === null || value === undefined) return '-';
  return `${value.toFixed(decimals)}%`;
};

// ==========================================
// ファイルサイズフォーマット
// ==========================================
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
};

// ==========================================
// ストレージ使用率
// ==========================================
export const calculateStoragePercentage = (used: number, limit: number): number => {
  if (limit === 0) return 0;
  return Math.min((used / limit) * 100, 100);
};

// ==========================================
// ドメイン検証
// ==========================================
export const normalizeDomain = (input: string): string => {
  let domain = input.trim();
  domain = domain.replace(/^https?:\/\//i, '');
  domain = domain.replace(/\/+$/, '');
  domain = domain.split('/')[0];
  return domain.toLowerCase();
};

export const isValidDomain = (input: string): boolean => {
  const domain = normalizeDomain(input);
  const domainRegex = /^(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/;
  return domainRegex.test(domain);
};

// ==========================================
// メールアドレス検証
// ==========================================
export const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

// ==========================================
// パスワード強度チェック
// ==========================================
export const checkPasswordStrength = (password: string): {
  isValid: boolean;
  score: number;
  feedback: string[];
} => {
  const feedback: string[] = [];
  let score = 0;

  if (password.length >= 8) {
    score += 1;
  } else {
    feedback.push('8文字以上必要です');
  }

  if (/[a-z]/.test(password)) {
    score += 1;
  } else {
    feedback.push('小文字を含めてください');
  }

  if (/[A-Z]/.test(password)) {
    score += 1;
  } else {
    feedback.push('大文字を含めてください');
  }

  if (/[0-9]/.test(password)) {
    score += 1;
  } else {
    feedback.push('数字を含めてください');
  }

  if (/[^a-zA-Z0-9]/.test(password)) {
    score += 1;
  } else {
    feedback.push('記号を含めてください');
  }

  return {
    isValid: feedback.length === 0,
    score,
    feedback,
  };
};

// ==========================================
// 文字列操作
// ==========================================
export const truncate = (str: string, length: number): string => {
  if (str.length <= length) return str;
  return str.slice(0, length) + '...';
};

export const maskApiKey = (apiKey: string): string => {
  if (apiKey.length <= 8) return '****';
  return `${apiKey.slice(0, 4)}****${apiKey.slice(-4)}`;
};

// ==========================================
// 色操作
// ==========================================
export const isValidHexColor = (color: string): boolean => {
  return /^#[0-9A-Fa-f]{6}$/.test(color);
};

// ==========================================
// クリップボード
// ==========================================
export const copyToClipboard = async (text: string): Promise<boolean> => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    // フォールバック
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    document.body.appendChild(textArea);
    textArea.select();
    try {
      document.execCommand('copy');
      return true;
    } catch {
      return false;
    } finally {
      document.body.removeChild(textArea);
    }
  }
};

// ==========================================
// URL生成
// ==========================================
export const generateEmbedCode = (apiKey: string): string => {
  return `<script src="${WIDGET_URL}/widget.js"></script>
<script>
  EntryBot.init({
    apiKey: '${apiKey}'
  });
</script>`;
};

// ==========================================
// エラーメッセージ取得
// ==========================================
export const getErrorMessage = (error: unknown): string => {
  if (error instanceof Error) {
    return error.message;
  }
  if (typeof error === 'string') {
    return error;
  }
  return '予期しないエラーが発生しました';
};

// ==========================================
// ステータス表示用
// ==========================================
export const getStatusColor = (status: string): 'success' | 'warning' | 'error' | 'info' | 'default' => {
  switch (status) {
    case 'active':
    case 'synced':
    case 'valid':
    case 'completed':
      return 'success';
    case 'pending':
    case 'syncing':
    case 'unknown':
      return 'warning';
    case 'inactive':
    case 'suspended':
    case 'withdrawn':
    case 'error':
    case 'invalid':
    case 'failed':
      return 'error';
    case 'trialing':
    case 'past_due':
      return 'info';
    default:
      return 'default';
  }
};

export const getStatusLabel = (status: string): string => {
  const labels: Record<string, string> = {
    // ユーザーステータス
    active: '有効',
    suspended: '停止中',
    withdrawn: '退会済み',
    pending_deletion: '削除予定',
    // ドメインステータス
    inactive: '無効',
    // ファイルステータス
    pending: '学習待ち',
    syncing: '学習中',
    synced: '学習済み',
    error: 'エラー',
    // APIキーステータス
    valid: '有効',
    invalid: '無効',
    unknown: '未検証',
    // 同期ステータス
    completed: '完了',
    failed: '失敗',
    // サブスクリプションステータス
    canceled: '解約済み',
    past_due: '支払い遅延',
    trialing: 'トライアル',
  };
  return labels[status] || status;
};
